import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { buildConfig } from '@/config';
import { useAuthStore } from '@/stores/authStore';

type Entry = { id: string; amount_micro_usd: number; balance_micro_usd: number; description: string; created_at: string; details: { machine_name?: string } };
type Account = { balance_micro_usd: number; tier: string; billing_enabled: boolean; billing_suspended: boolean; platform_billing_enabled: boolean; checkout_available: boolean; rates: { resource: string; unit: string; micro_usd: number }[]; entries: Entry[] };
const usd = (value: number): string => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(value / 1000000);
async function request<T>(path: string, data?: unknown): Promise<T> {
  const result = await fetch(`${buildConfig.apiBaseUrl}${path}`, { credentials: 'include',
    ...(data === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }) });
  const body = await result.json();
  if (!result.ok) throw new Error(body.error || 'Unable to load billing');
  return body;
}

export function Billing(): React.ReactElement {
  const authenticated = useAuthStore(s => s.isAuthenticated);
  const userId = useAuthStore(s => s.user?.id);
  const checked = useAuthStore(s => s.hasCheckedSession);
  const [amount, setAmount] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState('');
  const account = useQuery({ queryKey: ['billing', userId], queryFn: () => request<Account>('/billing'), enabled: authenticated, refetchInterval: 15000 });
  useEffect(() => { setRequestId(crypto.randomUUID()); }, [amount]);
  if (checked && !authenticated) return <p><Link to="/login">Log in</Link> to manage your credits.</p>;
  const data = account.data;
  async function purchase(): Promise<void> {
    setError('');
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) { setError('Enter dollars and cents.'); return; }
    const cents = Math.round(Number(amount) * 100);
    if (cents < 500 || cents > 50000) { setError('Choose an amount between $5 and $500.'); return; }
    setBusy(true);
    try {
      const result = await request<{ url: string }>('/billing/checkout', { amount_cents: cents, request_id: requestId });
      window.location.assign(result.url);
    } catch (err) { setError(err instanceof Error ? err.message : 'Checkout failed'); setBusy(false); }
  }
  return <div className="max-w-3xl flex flex-col gap-5">
    <h1 className="text-2xl font-semibold">Billing and credits</h1>
    {account.isError && <p role="alert">{account.error.message}</p>}
    {!data && !account.isError && <p>Loading…</p>}
    {data && <>
      <section className="themed-card p-5">
        <p className="text-xl">Credit balance: <strong>{usd(data.balance_micro_usd)}</strong></p>
        <p>{data.billing_suspended ? 'Payment dispute · account on hold' : data.tier === 'paid' ? 'Paid account' : data.billing_enabled ? (data.platform_billing_enabled ? 'Paid account · credits exhausted' : 'Prepaid credits held · billing paused') : 'Free account'}</p>
        {data.billing_suspended && <p className="mt-3" role="alert">Your account is on hold after a payment dispute. Contact support to resolve it; adding credits does not release the hold.</p>}
        <p className="mt-3">Prepaid credits cover CPU, data transfer, storage, and running desktops. Paid accounts have unlimited desktop connections and no account limit on public or private desktops.</p>
        <p className="mt-2">When credits run out, paid desktops stop and new launches wait for a top-up. Your stored desktops are retained. Usage is measured periodically, so the last interval may take the balance below zero. No automatic card charges.</p>
      </section>
      <section className="themed-card p-5">
        <h2 className="text-lg font-semibold">Add credits</h2>
        {data.checkout_available ? <>
          <label className="block my-2" htmlFor="credit-amount">Amount in USD ($5–$500)</label>
          <div className="flex gap-3"><input id="credit-amount" className="themed-input" value={amount} inputMode="decimal" disabled={busy} onChange={e => setAmount(e.target.value)} />
            <button className="btn-primary" disabled={busy || !requestId} onClick={purchase}>{busy ? 'Opening checkout…' : 'Continue to secure checkout'}</button></div>
          <p className="mt-3 text-sm">Credits appear after payment is confirmed. This page refreshes automatically; returning from checkout alone does not confirm payment.</p>
        </> : <p>Online credit purchases are not available yet. Existing credits and usage remain visible here.</p>}
        {error && <p role="alert" className="mt-3">{error}</p>}
      </section>
      <section><h2 className="text-lg font-semibold">Current usage rates</h2>
        {data.rates.length ? <ul>{data.rates.map(rate => <li key={rate.resource}>{usd(rate.micro_usd)} per {rate.unit}</li>)}</ul> : <p>Usage prices have not been configured.</p>}
      </section>
      <section><h2 className="text-lg font-semibold">Recent activity</h2>
        {!data.entries.length ? <p>No credit activity yet.</p> : <div className="overflow-x-auto"><table className="w-full text-left text-sm">
          <thead><tr><th>Date</th><th>Activity</th><th>Amount</th><th>Balance</th></tr></thead>
          <tbody>{data.entries.map(entry => <tr key={entry.id}><td className="py-2">{new Date(entry.created_at).toLocaleString()}</td><td>{entry.description}{entry.details.machine_name ? ` · ${entry.details.machine_name}` : ''}</td><td>{usd(entry.amount_micro_usd)}</td><td>{usd(entry.balance_micro_usd)}</td></tr>)}</tbody>
        </table></div>}
        <p className="text-sm mt-2">Showing the latest 100 entries. Amounts use USD microcredits (one million per dollar).</p>
      </section>
    </>}
  </div>;
}
